import { addRow, canAddRow, removeRowAt, setRowAt } from './listRowHelpers';

// No default selection — the user must choose a send condition and enter days.
export function makeSendCondition() {
  return { sendCondition: '', day: '' };
}

export function getInitialSendConditions(values) {
  if (Array.isArray(values) && values.length > 0) {
    return values.map((item) => ({
      sendCondition: String(item?.sendCondition ?? ''),
      day: String(item?.day ?? ''),
    }));
  }
  return [makeSendCondition()];
}

export function setSendConditionAt({ items, index, patch }) {
  return setRowAt(items, index, (row) => ({ ...row, ...patch }));
}

export function removeSendConditionAt({ items, index }) {
  return removeRowAt(items, index, makeSendCondition);
}

export function addSendCondition({ items }) {
  return addRow(items, makeSendCondition);
}

// A row must be complete before another can be added. When Days is optional for the
// last row (admins, or an untouched migrated row in edit mode) only the send condition
// needs a value there.
export function canAddSendCondition(items, { lastRowDayOptional = false } = {}) {
  return canAddRow(items, (row) => {
    const hasSendCondition = Boolean(String(row?.sendCondition ?? '').trim());
    const hasDay = Boolean(String(row?.day ?? '').trim());
    return lastRowDayOptional ? hasSendCondition : hasSendCondition && hasDay;
  });
}

// Per-row flag: may this row's Days field be left empty on save/publish/deactivate?
//  - Admins: always yes.
//  - Otherwise only while editing an existing template ("grandfathering" migrated rows
//    that were saved with no day): yes for a row whose day was empty as loaded, as long as
//    the trigger is unchanged and this row's send condition is unchanged. Changing the
//    trigger (template-wide) revokes it for every row; changing a row's send condition
//    revokes it for that row. Never granted when creating a new template.
export function computeDayOptionalByRow({
  isAdmin = false,
  isEditMode = false,
  triggerId,
  sendConditions,
  initialTriggerId,
  initialSendConditions,
}) {
  const rows = Array.isArray(sendConditions) ? sendConditions : [];
  if (isAdmin) return rows.map(() => true);
  if (!isEditMode) return rows.map(() => false);

  const triggerUnchanged = String(triggerId ?? '') === String(initialTriggerId ?? '');
  const initialRows = Array.isArray(initialSendConditions) ? initialSendConditions : [];

  return rows.map((row, index) => {
    if (!triggerUnchanged) return false;
    const initialRow = initialRows[index];
    if (!initialRow) return false; // a newly added row follows the normal rules
    const initialDayEmpty = String(initialRow.day ?? '').trim().length === 0;
    if (!initialDayEmpty) return false; // a row that had a day keeps requiring one
    return String(row?.sendCondition ?? '') === String(initialRow.sendCondition ?? '');
  });
}

// Digits only; reject anything outside 1–30 by keeping the previous value.
export function sanitizeDayInput(value, prev = '') {
  const digits = String(value ?? '')
    .replace(/[^\d]/g, '')
    .slice(0, 2);
  if (digits === '') return '';
  const num = Number(digits);
  return num >= 1 && num <= 30 ? digits : prev;
}

// When the trigger section (Trigger condition + Send conditions + Days) must be
// filled: always on publish; on save/deactivate only once the user has started it
// (so a fully-empty section can still be saved as a draft).
export function isTriggerSectionRequired({
  isTrigger,
  triggerId,
  sendConditions,
  publishAttempt,
  draftAttempt,
}) {
  if (!isTrigger) return false;
  const triggerFilled = String(triggerId ?? '').trim().length > 0;
  const anySendValue = (sendConditions ?? []).some(
    (row) => String(row?.sendCondition ?? '').trim() || String(row?.day ?? '').trim(),
  );
  return Boolean(publishAttempt || (draftAttempt && (triggerFilled || anySendValue)));
}
